/* eslint-env es2020 */
import { isNativeCalendarDate } from "./nativeScalar.mjs";
import { compileReportParameters, resolveReportParameterValues } from "./nativeParameters.mjs";

// Shared arithmetic authority. Preserve the historical v3.1 coercion policy.
const record = (value) => value && typeof value === "object" && !Array.isArray(value) ? value : {};
const path = (value, name) => name ? name.split(".").reduce((current, key) => record(current)[key], value) : undefined;
export const asExpression = (value) => value && typeof value === "object" && !Array.isArray(value) ? value : undefined;

export function normalizeAggregateSpec(value, column, defaultScope) {
  if (!value) return null;
  if (typeof value === "string") return { fn: value, field: column?.binding || column?.key || column?.id, scope: defaultScope, format: column?.format };
  const spec = record(value);
  return {
    fn: String(spec.fn || spec.function || ""),
    field: typeof spec.field === "string" ? spec.field : column?.binding || column?.key || column?.id,
    expr: asExpression(spec.expr || spec.expression),
    scope: spec.scope === "group" ? "group" : "report",
    format: typeof spec.format === "string" ? spec.format : undefined,
  };
}

export function evaluateAggregate(aggregate, context) {
  const rows = aggregate.scope === "group" && context.currentGroupId
    ? context.groupRowsById.get(context.currentGroupId) || []
    : context.reportRows;
  if (aggregate.fn === "count") return rows.length;
  const values = rows
    .map((row) => aggregate.expr ? evaluateNativeExpression(aggregate.expr, { row }) : path(row, aggregate.field))
    .filter((value) => value !== null && value !== undefined && value !== "")
    .map(Number)
    .filter(Number.isFinite);
  if (aggregate.fn === "sum") return values.reduce((sum, value) => sum + value, 0);
  if (aggregate.fn === "min") return values.length ? Math.min(...values) : null;
  if (aggregate.fn === "max") return values.length ? Math.max(...values) : null;
  if (aggregate.fn === "avg") return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;
  return null;
}

export function evaluateNativeExpression(expression, context = {}) {
  const expr = record(expression);
  if (["typed", "call", "calc", "param"].some(key => Object.prototype.hasOwnProperty.call(expr, key))) {
    expressionFail("TARGET", "$.expr", "Typed expressions require the explicit shared runtime; native target execution is unavailable.");
  }
  if (Object.prototype.hasOwnProperty.call(expr, "const")) return expr.const;
  if (typeof expr.field === "string") return path(context.row, expr.field);
  if (expr.aggregate) return evaluateAggregate(normalizeAggregateSpec(expr.aggregate, undefined, "report"), context.aggregateContext || { reportRows: [], groupRowsById: new Map() });
  const op = String(expr.op || "");
  const args = Array.isArray(expr.args) ? expr.args : [expr.left, expr.right].filter(Boolean);
  const values = args.map((item) => evaluateNativeExpression(item, context));
  if (op === "add") return values.reduce((sum, value) => sum + numericOperand(value), 0);
  if (op === "sub") return values.length <= 1 ? -numericOperand(values[0]) : values.slice(1).reduce((result, value) => result - numericOperand(value), numericOperand(values[0]));
  if (op === "mul") return values.reduce((result, value) => result * numericOperand(value), 1);
  if (op === "div") {
    const numerator = numericOperand(values[0]);
    const denominator = numericOperand(values[1]);
    return denominator === 0 ? null : numerator / denominator;
  }
  return null;
}

function numericOperand(value) {
  if (value === null || value === undefined || value === "") return 0;
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

export const TYPED_EXPRESSIONS_CAPABILITY = "typed-expressions-v1";
export const TYPED_EXPRESSION_LIMITS = Object.freeze({
  depth: 16, nodes: 256, definitionNodes: 16384, arguments: 16,
  literalLength: 1024, pathLength: 256, stringLength: 16384,
  calculations: 64, declarations: 128, dependencyDepth: 16, dependencies: 256,
  work: 2000000, retainedBytes: 16 * 1024 * 1024,
});
const registry = {
  concat: [2, 16, "string"], trim: [1, 1, "string"], length: [1, 1, "string"],
  abs: [1, 1, "number"], round: [2, 2, "round"], floor: [1, 1, "number"],
  ceiling: [1, 1, "number"], min: [1, 16, "number"], max: [1, 16, "number"],
  and: [2, 16, "boolean"], or: [2, 16, "boolean"], not: [1, 1, "boolean"],
  eq: [2, 2, "equality"], neq: [2, 2, "equality"],
  gt: [2, 2, "ordered"], gte: [2, 2, "ordered"], lt: [2, 2, "ordered"], lte: [2, 2, "ordered"],
  if: [3, 3, "selection"], is_null: [1, 1, "null"], coalesce: [2, 16, "coalesce"], nullif: [2, 2, "equality"],
  year: [1, 1, "date"], month: [1, 1, "date"], day: [1, 1, "date"],
  date_add: [3, 3, "dateAdd"], date_diff: [3, 3, "dateDiff"],
};
export const TYPED_FUNCTIONS = Object.freeze(Object.fromEntries(Object.entries(registry).map(([id, [minArgs, maxArgs, family]]) =>
  [id, Object.freeze({ minArgs, maxArgs, family })])));
Object.freeze(registry);
const ownTyped = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
const scalarTypes = ["string", "number", "boolean", "date"];
const expressionScopes = ["document", "dataset", "detail", "group", "cell"];
const arithmetic = Object.freeze({ add: [2, 16], sub: [1, 2], mul: [2, 16], div: [2, 2] });
export const TYPED_OPERATORS = Object.freeze(Object.fromEntries(Object.entries(arithmetic).map(([id, [minArgs, maxArgs]]) =>
  [id, Object.freeze({ minArgs, maxArgs, family: "number" })])));
// Authoring hints only. The compiler remains authoritative, including cross-argument constraints.
export function typedArgumentContract(kind, id, index) {
  const signature = kind === "op" ? TYPED_OPERATORS[id] : TYPED_FUNCTIONS[id];
  if (!signature || !Number.isInteger(index) || index < 0 || index >= signature.maxArgs) return null;
  const family = signature.family;
  let types = [...scalarTypes], nullable = true, choices;
  if (["string", "number", "boolean", "date"].includes(family)) {
    types = [family];
    nullable = family !== "boolean";
  } else if (family === "ordered") { types = ["number", "date"]; nullable = false; }
  else if (family === "selection" && index === 0) { types = ["boolean"]; nullable = false; }
  else if (family === "round") { types = ["number"]; nullable = index === 0; }
  else if (family === "dateAdd" || family === "dateDiff") {
    types = [index === 2 ? "string" : index === 0 || family === "dateDiff" ? "date" : "number"];
    nullable = index !== 2 && (index === 0 || family === "dateDiff");
    if (index === 2) choices = ["day"];
  }
  return Object.freeze({ types: Object.freeze(types), nullable, ...(choices ? { choices: Object.freeze(choices) } : {}) });
}
const compiledKey = Symbol("compiled native typed expression");
const sessionKey = Symbol("native typed execution session");
// eslint-disable-next-line no-control-regex -- The fixed protocol whitespace includes tab/newline.
const trimStartPattern = /^[\u0009-\u000d\u0020\u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+/;
// eslint-disable-next-line no-control-regex -- The fixed protocol whitespace includes tab/newline.
const trimEndPattern = /[\u0009-\u000d\u0020\u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+$/;

export class NativeExpressionError extends Error {
  constructor(code, location, message) {
    super(message);
    this.name = "NativeExpressionError";
    this.code = code;
    this.path = location.slice(0, 512);
    this.status = code === "NATIVE_EXPRESSION_LIMIT" ? 413 : 400;
  }
}
function expressionFail(suffix, location, message) { throw new NativeExpressionError(`NATIVE_EXPRESSION_${suffix}`, location, message); }
function reducedLimits(options = {}) {
  typedObject(options, "$.limits");
  const limits = { ...TYPED_EXPRESSION_LIMITS };
  for (const [key, value] of Object.entries(options)) {
    if (!ownTyped(limits, key) || !Number.isSafeInteger(value) || value < 1 || value > limits[key]) expressionFail("LIMIT", "$.limits", "Limits may only reduce supported positive budgets.");
    limits[key] = value;
  }
  return limits;
}
function standardDataPrototype(prototype, standard) {
  if (prototype === standard) return true;
  if (!prototype || (standard === Object.prototype && Object.getPrototypeOf(prototype) !== null)) return false;
  const expected = Reflect.ownKeys(standard);
  if (Reflect.ownKeys(prototype).length !== expected.length) return false;
  return expected.every(key => {
    const left = Object.getOwnPropertyDescriptor(prototype, key);
    const right = Object.getOwnPropertyDescriptor(standard, key);
    if (!left || left.enumerable !== right.enumerable || ownTyped(left, "value") !== ownTyped(right, "value")) return false;
    return (ownTyped(right, "value") ? ["value"] : ["get", "set"]).every(member =>
      typeof left[member] === typeof right[member] && (typeof right[member] === "function"
        ? Function.prototype.toString.call(left[member]) === Function.prototype.toString.call(right[member]) : left[member] === right[member]));
  });
}
function typedObject(value, location) {
  if (!value || typeof value !== "object" || Array.isArray(value)) expressionFail("INVALID", location, "Expected a plain declarative object.");
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== null && !standardDataPrototype(prototype, Object.prototype)) expressionFail("INVALID", location, "Expected a plain declarative object.");
  for (const key of Reflect.ownKeys(value)) {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (typeof key !== "string" || !descriptor.enumerable || !ownTyped(descriptor, "value") || ["__proto__", "prototype", "constructor"].includes(key)) {
      expressionFail("INVALID", location, "Executable, hidden or unsafe properties are unsupported.");
    }
  }
  return value;
}
function typedKeys(value, allowed, location) {
  typedObject(value, location);
  if (Object.keys(value).some(key => !allowed.includes(key))) expressionFail("INVALID", location, "Unexpected expression member.");
}
function typedArray(value, maximum, location) {
  if (!Array.isArray(value) || !standardDataPrototype(Object.getPrototypeOf(value), Array.prototype)) expressionFail("INVALID", location, "Expected a plain declarative array.");
  if (value.length > maximum) expressionFail("LIMIT", location, "Expression array limit exceeded.");
  for (const key of Reflect.ownKeys(value)) {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (typeof key !== "string" || (key !== "length" && !/^(0|[1-9]\d*)$/.test(key)) || !ownTyped(descriptor, "value")) expressionFail("INVALID", location, "Unexpected array property.");
  }
  for (let index = 0; index < value.length; index++) if (!ownTyped(value, index)) expressionFail("INVALID", location, "Sparse arrays are unsupported.");
  return value;
}
export function assertTypedData(value, maximum) {
  return maximum === undefined ? typedObject(value, "$.data") : typedArray(value, maximum, "$.data");
}
function typedPath(value, location, limits, identity = false) {
  if (typeof value !== "string" || value.length > (identity ? 64 : limits.pathLength)
    || !(identity ? /^[A-Za-z_][A-Za-z0-9_-]*$/ : /^[A-Za-z_][A-Za-z0-9_-]*(?:\.[A-Za-z_][A-Za-z0-9_-]*)*$/).test(value)
    || value.split(".").some(part => ["__proto__", "prototype", "constructor"].includes(part))) expressionFail("REFERENCE", location, "Expected a bounded safe reference.");
}
function validString(value, maximum, location, suffix = "DATA") {
  if (value.length > maximum) expressionFail("LIMIT", location, "Expression string limit exceeded.");
  // eslint-disable-next-line no-control-regex -- Reject the XML-disallowed control code points.
  if (/[\u0000-\u0008\u000b\u000c\u000e-\u001f\ufffe\uffff]/.test(value) || /[\ud800-\udfff]/u.test(value)) expressionFail(suffix, location, "Expected XML-safe Unicode text.");
}
function typeInfo(type, nullable = false) { return Object.freeze({ type, nullable }); }
function parseTypedRoot(root, limits) {
  typedKeys(root, ["typed"], "$.expr");
  if (!ownTyped(root, "typed")) expressionFail("INVALID", "$.expr", "Expected a typed expression root.");
  let nodes = 0; let bytes = 64;
  const references = new Set();
  function parse(value, depth, location) {
    if (++nodes > limits.nodes || depth > limits.depth) expressionFail("LIMIT", location, "Expression depth or node limit exceeded.");
    typedObject(value, location);
    const kinds = ["const", "field", "calc", "param", "op", "call", "aggregate"].filter(key => ownTyped(value, key));
    if (kinds.length !== 1) expressionFail("INVALID", location, "Expected exactly one expression discriminator.");
    const kind = kinds[0];
    const allowed = { const: ["const", "valueType"], field: ["field", "scope", "valueType"], calc: ["calc"], param: ["param"],
      op: ["op", "args"], call: ["call", "args"], aggregate: ["aggregate"] }[kind];
    typedKeys(value, allowed, location);
    bytes += 128;
    const node = { kind, location, canonical: {} };
    if (kind === "const") {
      const literal = Object.is(value.const, -0) ? 0 : value.const;
      if (literal !== null && !["string", "number", "boolean"].includes(typeof literal)) expressionFail("LITERAL", location, "Expected a scalar JSON literal.");
      if (typeof literal === "number" && !Number.isFinite(literal)) expressionFail("LITERAL", location, "Expected a finite JSON number.");
      if (typeof literal === "string") { validString(literal, Math.min(limits.literalLength, limits.stringLength), location, "LITERAL"); bytes += literal.length * 2; }
      const declared = value.valueType;
      if (declared !== undefined && (!scalarTypes.includes(declared) || (literal !== null && (declared === "date" ? !isNativeCalendarDate(literal) : typeof literal !== declared)))) expressionFail("LITERAL", location, "Literal does not match its declared type.");
      node.value = literal;
      node.result = typeInfo(declared || (literal === null ? "null" : typeof literal), literal === null);
      node.canonical = { const: literal, ...(declared === undefined ? {} : { valueType: declared }) };
    } else if (kind === "field") {
      typedPath(value.field, location, limits);
      if (!expressionScopes.includes(value.scope)) expressionFail("SCOPE", location, "Field scope is unsupported.");
      if (value.valueType !== undefined && !scalarTypes.includes(value.valueType)) expressionFail("TYPE", location, "Unsupported field type.");
      node.field = value.field; node.scope = value.scope; node.declared = value.valueType;
      node.canonical = { field: value.field, scope: value.scope, ...(value.valueType === undefined ? {} : { valueType: value.valueType }) };
      bytes += value.field.length * 2;
    } else if (kind === "param") {
      if (typeof value.param !== "string" || !/^[A-Za-z][A-Za-z0-9_-]{0,63}$/.test(value.param)) expressionFail("REFERENCE", location, "Expected a bounded parameter identifier.");
      node.id = value.param; node.canonical = { param: value.param };
      bytes += value.param.length * 2;
    } else if (kind === "calc") {
      typedPath(value.calc, location, limits, true);
      references.add(value.calc);
      node.id = value.calc; node.canonical = { calc: value.calc };
    } else if (kind === "aggregate") {
      const aggregate = value.aggregate;
      typedKeys(aggregate, ["fn", "scope", "field", "expr"], location);
      if (!["sum", "count", "min", "max", "avg"].includes(aggregate.fn)) expressionFail("UNSUPPORTED", location, "Unsupported aggregate function.");
      if (!["report", "group"].includes(aggregate.scope)) expressionFail("SCOPE", location, "Aggregate scope must be explicit.");
      const sources = Number(ownTyped(aggregate, "field")) + Number(ownTyped(aggregate, "expr"));
      if (sources !== (aggregate.fn === "count" ? 0 : 1)) expressionFail("INVALID", location, "Aggregate requires exactly its supported source.");
      if (ownTyped(aggregate, "field")) typedPath(aggregate.field, location, limits);
      node.aggregate = { ...aggregate };
      node.args = ownTyped(aggregate, "expr") ? [parse(aggregate.expr, depth + 1, `${location}.aggregate.expr`)] : [];
      node.canonical = { aggregate: { ...aggregate, ...(node.args.length ? { expr: node.args[0].canonical } : {}) } };
    } else {
      const id = value[kind];
      if (typeof id !== "string" || !(kind === "call" ? ownTyped(TYPED_FUNCTIONS, id) : ownTyped(arithmetic, id))) expressionFail("UNSUPPORTED", location, "Unsupported typed expression function.");
      const [minimum, maximum] = kind === "call" ? [TYPED_FUNCTIONS[id].minArgs, TYPED_FUNCTIONS[id].maxArgs] : arithmetic[id];
      typedArray(value.args, limits.arguments, `${location}.args`);
      if (value.args.length < minimum || value.args.length > maximum) expressionFail("ARITY", location, "Function argument count is invalid.");
      node.id = id;
      node.args = value.args.map((arg, index) => parse(arg, depth + 1, `${location}.args[${index}]`));
      node.canonical = { [kind]: id, args: node.args.map(arg => arg.canonical) };
    }
    if (bytes > limits.retainedBytes) expressionFail("LIMIT", location, "Compiled expression retention budget exceeded.");
    return node;
  }
  const tree = parse(root.typed, 1, "$.expr.typed");
  return { tree, nodes, bytes, references, canonical: { typed: tree.canonical } };
}
function fieldCatalog(fields, limits) {
  typedObject(fields, "$.fields");
  const result = Object.create(null);
  for (const scope of Object.keys(fields)) {
    if (!expressionScopes.includes(scope)) expressionFail("SCOPE", "$.fields", "Unsupported field catalog scope.");
    typedObject(fields[scope], "$.fields");
    const entries = Object.entries(fields[scope]);
    if (entries.length > limits.definitionNodes) expressionFail("LIMIT", "$.fields", "Field catalog limit exceeded.");
    result[scope] = Object.create(null);
    for (const [name, descriptor] of entries) {
      typedPath(name, "$.fields", limits);
      if ((scope === "group" && name !== "key") || (scope === "cell" && name !== "value")) expressionFail("SCOPE", "$.fields", "Unsupported scoped field.");
      typedKeys(descriptor, ["type", "nullable"], "$.fields");
      if (!scalarTypes.includes(descriptor.type) || typeof descriptor.nullable !== "boolean") expressionFail("TYPE", "$.fields", "Fields require an explicit scalar type and nullability.");
      result[scope][name] = typeInfo(descriptor.type, descriptor.nullable);
    }
  }
  return result;
}
function compatible(actual, expected, nullable, location) {
  if ((actual.type !== expected && actual.type !== "null") || (!nullable && actual.nullable)) expressionFail("TYPE", location, "Argument type or nullability is incompatible.");
}
function unify(types, location) {
  const names = [...new Set(types.map(item => item.type).filter(type => type !== "null"))];
  if (names.length > 1) expressionFail("TYPE", location, "Arguments require matching scalar types.");
  return typeInfo(names[0] || "null", types.some(item => item.nullable));
}
function inferTyped(tree, fields, allowed, calculations, parameters) {
  function infer(node) {
    const location = node.location;
    if (node.kind === "const") return node.result;
    if (node.kind === "field") {
      if (!allowed.includes(node.scope)) expressionFail("SCOPE", location, "Field is unavailable in this expression scope.");
      const descriptor = fields[node.scope]?.[node.field];
      if (!descriptor) expressionFail("REFERENCE", location, "Field is absent from the closed source contract.");
      if (node.declared !== undefined && node.declared !== descriptor.type) expressionFail("TYPE", location, "Field annotation conflicts with its source type.");
      node.result = descriptor;
    } else if (node.kind === "param") {
      const parameter = parameters.find(parameter => parameter.id === node.id);
      if (!parameter) expressionFail("REFERENCE", location, "Parameter is not declared.");
      node.result = typeInfo(parameter.type, parameter.nullable);
    } else if (node.kind === "calc") {
      if (!allowed.includes("detail")) expressionFail("SCOPE", location, "Calculated reference requires a detail context.");
      if (!ownTyped(calculations, node.id)) expressionFail("REFERENCE", location, "Calculated reference is unavailable.");
      node.result = calculations[node.id];
    } else if (node.kind === "aggregate") {
      if (!allowed.includes("dataset") || (node.aggregate.scope === "group" && !allowed.includes("group"))) expressionFail("SCOPE", location, "Aggregate is unavailable in this target scope.");
      if (node.aggregate.field && !fields.detail?.[node.aggregate.field]) expressionFail("REFERENCE", location, "Aggregate source is absent from the closed detail contract.");
      if (node.aggregate.field && fields.detail[node.aggregate.field].type !== "number") expressionFail("TYPE", location, "Aggregate source must be a declared numeric detail field.");
      if (node.args.length) {
        const result = inferTyped(node.args[0], fields, ["detail", "document"], calculations, parameters);
        compatible(result, "number", true, location);
      }
      expressionFail("AGGREGATE_UNAVAILABLE", location, "Typed aggregate execution requires later summary integration.");
    } else {
      const args = node.args.map(infer);
      const id = node.id;
      const family = node.kind === "op" ? "number" : TYPED_FUNCTIONS[id].family;
      let result;
      if (["number", "string", "date"].includes(family)) {
        args.forEach(arg => compatible(arg, family, true, location));
        result = typeInfo(["length", "year", "month", "day"].includes(id) ? "number" : family, args.some(arg => arg.nullable));
      } else if (family === "boolean") {
        args.forEach(arg => compatible(arg, "boolean", false, location));
        result = typeInfo("boolean");
      } else if (family === "equality") {
        const common = unify(args, location);
        result = id === "nullif" ? typeInfo(common.type, true) : typeInfo("boolean");
      } else if (family === "ordered") {
        const common = unify(args, location);
        if (common.nullable || !["number", "date"].includes(common.type)) expressionFail("TYPE", location, "Ordering requires matching nonnullable numbers or dates.");
        result = typeInfo("boolean");
      } else if (family === "selection") {
        compatible(args[0], "boolean", false, location);
        result = unify(args.slice(1), location);
      } else if (family === "coalesce") {
        const common = unify(args, location);
        result = typeInfo(common.type, args.every(arg => arg.nullable));
      } else if (family === "null") result = typeInfo("boolean");
      else {
        compatible(args[0], family === "round" ? "number" : "date", true, location);
        compatible(args[1], family === "dateDiff" ? "date" : "number", family === "dateDiff", location);
        if (family === "round" || family === "dateAdd") {
          node.refinement = family === "round" ? 6 : 3652058;
          if (node.args[1].kind === "const") integerArgument(node.args[1].value, node.refinement, location);
        }
        if (family !== "round" && (node.args[2].kind !== "const" || node.args[2].value !== "day")) expressionFail("LITERAL", location, "Date unit must be the literal day.");
        result = typeInfo(family === "dateAdd" ? "date" : "number", args[0].nullable || (family === "dateDiff" && args[1].nullable));
      }
      node.result = result;
    }
    return node.result;
  }
  return infer(tree);
}
function integerArgument(value, maximum, location) {
  if (!Number.isSafeInteger(value) || Math.abs(value) > maximum) expressionFail("RANGE", location, "Integer argument exceeds the supported range.");
}
function freezeTree(value) {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    Object.values(value).forEach(freezeTree);
    Object.freeze(value);
  }
  return value;
}
function compileParsed(parsed, fields, allowed, calculations, limits, parameters = []) {
  const result = inferTyped(parsed.tree, fields, allowed, calculations, parameters);
  if (result.type === "null") expressionFail("TYPE", "$.expr", "A null-only result requires an explicit valueType.");
  freezeTree(parsed.tree);
  return Object.freeze({ type: result, canonical: freezeTree(parsed.canonical),
    [compiledKey]: Object.freeze({ ...parsed, limits, parameters }) });
}
export function compileTypedExpression(root, options = {}) {
  const limits = reducedLimits(options.limits);
  const fields = fieldCatalog(options.fields || {}, limits);
  const allowed = options.allowedScopes || ["detail", "document"];
  typedArray(allowed, expressionScopes.length, "$.allowedScopes");
  if (allowed.some(scope => !expressionScopes.includes(scope))) expressionFail("SCOPE", "$.allowedScopes", "Unsupported available scope.");
  return compileParsed(parseTypedRoot(root, limits), fields, allowed, Object.create(null), limits, compileReportParameters(options.parameters));
}
export function serializeTypedExpression(root, options = {}) { return JSON.stringify(compileTypedExpression(root, options).canonical); }
export function inspectTypedExpressionStructure(root) {
  const parsed = parseTypedRoot(root, TYPED_EXPRESSION_LIMITS);
  let documentNodes = 0;
  let hasParameters = false;
  const pending = [parsed.canonical];
  while (pending.length) {
    const value = pending.pop(); documentNodes++;
    if (value && typeof value === "object" && ownTyped(value, "param")) hasParameters = true;
    if (value && typeof value === "object") pending.push(...Object.values(value));
  }
  return { nodes: parsed.nodes, documentNodes, hasParameters };
}

function spend(state, amount, location) {
  if (!Number.isSafeInteger(amount) || amount < 0 || amount > state.limits.work - state.work) expressionFail("LIMIT", location, "Expression execution work budget exceeded.");
  state.work += amount;
}
function reserve(state, amount, location) {
  if (!Number.isSafeInteger(amount) || amount < 0 || amount > state.limits.retainedBytes - state.bytes - state.temporaryBytes) expressionFail("LIMIT", location, "Expression derived-value budget exceeded.");
}
function allocateTemporary(state, amount, location) { reserve(state, amount, location); state.temporaryBytes += amount; }
function scalarData(value, descriptor, state, location) {
  if (value === null) {
    if (!descriptor.nullable) expressionFail("DATA", location, "Execution value violates declared nullability.");
    return value;
  }
  if (descriptor.type === "date" ? !isNativeCalendarDate(value) : typeof value !== descriptor.type) expressionFail("DATA", location, "Execution value does not match its declared scalar type.");
  if (typeof value === "number" && !Number.isFinite(value)) expressionFail("DATA", location, "Execution value must be finite.");
  if (typeof value === "string") {
    if (value.length > state.limits.stringLength) expressionFail("LIMIT", location, "Execution string limit exceeded.");
    spend(state, Math.ceil(value.length / 64), location);
    validString(value, state.limits.stringLength, location);
  }
  return Object.is(value, -0) ? 0 : value;
}
function readTypedField(context, node, state) {
  let current = ownTyped(context, node.scope) ? Object.getOwnPropertyDescriptor(context, node.scope) : undefined;
  if (!current || !ownTyped(current, "value")) expressionFail("DATA", node.location, "Execution scope is unavailable.");
  let value = current.value;
  for (const part of node.field.split(".")) {
    if (!value || typeof value !== "object" || Array.isArray(value)
      || (Object.getPrototypeOf(value) !== null && !standardDataPrototype(Object.getPrototypeOf(value), Object.prototype))) expressionFail("DATA", node.location, "Execution field is unavailable.");
    current = Object.getOwnPropertyDescriptor(value, part);
    if (!current || !ownTyped(current, "value") || !current.enumerable || current.value === undefined) expressionFail("DATA", node.location, "Execution field is unavailable.");
    value = current.value;
  }
  return scalarData(value, node.result, state, node.location);
}
function ordinal(value) {
  const [year, month, day] = value.split("-").map(Number);
  const previous = year - 1;
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  return previous * 365 + Math.floor(previous / 4) - Math.floor(previous / 100) + Math.floor(previous / 400)
    + [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334][month - 1] + (leap && month > 2 ? 1 : 0) + day - 1;
}
function calendarFromOrdinal(value, location) {
  if (!Number.isSafeInteger(value) || value < 0 || value > 3652058) expressionFail("RANGE", location, "Date result is outside the supported calendar.");
  let low = 1; let high = 9999;
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    if (ordinal(`${String(middle).padStart(4, "0")}-01-01`) <= value) low = middle + 1;
    else high = middle - 1;
  }
  const year = String(high).padStart(4, "0");
  let month = 1;
  while (month < 12 && ordinal(`${year}-${String(month + 1).padStart(2, "0")}-01`) <= value) month++;
  const day = value - ordinal(`${year}-${String(month).padStart(2, "0")}-01`) + 1;
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}
function decimalRound(value, precision) {
  const [mantissa, exponent = "0"] = Math.abs(value).toString().split("e");
  const fractional = mantissa.split(".")[1]?.length || 0;
  const shift = Number(exponent) - fractional + precision;
  if (shift >= 0 || value === 0) return value;
  const integer = globalThis.BigInt;
  const coefficient = integer(mantissa.replace(".", ""));
  const divisor = integer(10) ** integer(-shift);
  const rounded = coefficient / divisor + (coefficient % divisor * integer(2) >= divisor ? integer(1) : integer(0));
  return (value < 0 ? -1 : 1) * Number(`${rounded}e${-precision}`);
}
function runTyped(node, context, calculations, state) {
  spend(state, 1, node.location);
  if (node.kind === "const") return node.value;
  if (node.kind === "field") return readTypedField(context, node, state);
  if (node.kind === "param") {
    const values = state.currentParameters;
    if (!ownTyped(values, node.id)) expressionFail("DATA", node.location, "Optional nonnullable parameter has no execution value.");
    return scalarData(values[node.id], node.result, state, node.location);
  }
  if (node.kind === "calc") {
    if (!ownTyped(calculations, node.id)) expressionFail("DATA", node.location, "Calculated execution value is unavailable.");
    return scalarData(calculations[node.id], node.result, state, node.location);
  }
  const id = node.id;
  if (id === "if") return runTyped(node.args[runTyped(node.args[0], context, calculations, state) ? 1 : 2], context, calculations, state);
  if (id === "and" || id === "or") {
    for (const arg of node.args) {
      const value = runTyped(arg, context, calculations, state);
      if (id === "and" && value === false) return false;
      if (id === "or" && value === true) return true;
    }
    return id === "and";
  }
  if (id === "coalesce") {
    for (const arg of node.args) {
      const value = runTyped(arg, context, calculations, state);
      if (value !== null) return value;
    }
    return null;
  }
  const values = node.args.map(arg => runTyped(arg, context, calculations, state));
  const [left, right] = values;
  if (id === "is_null") return left === null;
  if (id === "eq" || id === "neq" || id === "nullif") {
    if (typeof left === "string" && typeof right === "string") spend(state, Math.ceil(Math.max(left.length, right.length) / 64), node.location);
    return id === "nullif" ? left === right ? null : left : id === "eq" ? left === right : left !== right;
  }
  if (id === "not") return !left;
  if (id === "gt") return left > right;
  if (id === "gte") return left >= right;
  if (id === "lt") return left < right;
  if (id === "lte") return left <= right;
  if (node.refinement !== undefined) integerArgument(right, node.refinement, node.location);
  if (values.includes(null)) return null;
  let result;
  if (node.kind === "op") {
    if (id === "add") result = values.reduce((total, value) => finiteResult(total + value, node.location), 0);
    if (id === "mul") result = values.reduce((total, value) => finiteResult(total * value, node.location), 1);
    if (id === "sub") result = values.length === 1 ? -left : left - right;
    if (id === "div") {
      if (right === 0) expressionFail("DIV_ZERO", node.location, "Expression division by zero.");
      result = left / right;
    }
  } else if (id === "concat") {
    const length = values.reduce((size, value) => size + value.length, 0);
    if (length > state.limits.stringLength) expressionFail("LIMIT", node.location, "Expression result string limit exceeded.");
    spend(state, Math.ceil(length / 64), node.location); allocateTemporary(state, length * 2 + 64, node.location);
    result = values.join("");
  } else if (id === "trim" || id === "length") {
    spend(state, Math.ceil(left.length / 64), node.location);
    if (id === "trim") { spend(state, Math.ceil(left.length / 64), node.location); allocateTemporary(state, left.length * 4 + 128, node.location); result = left.replace(trimStartPattern, "").replace(trimEndPattern, ""); }
    else {
      result = 0;
      for (let index = 0; index < left.length; result++) index += left.codePointAt(index) > 0xffff ? 2 : 1;
    }
  } else if (id === "abs") result = Math.abs(left);
  else if (id === "floor") result = Math.floor(left);
  else if (id === "ceiling") result = Math.ceil(left);
  else if (id === "min") result = Math.min(...values);
  else if (id === "max") result = Math.max(...values);
  else if (id === "round") { spend(state, 384, node.location); result = decimalRound(left, right); }
  else if (["year", "month", "day"].includes(id)) result = Number(left.split("-")[{ year: 0, month: 1, day: 2 }[id]]);
  else if (id === "date_add") { spend(state, 32, node.location); allocateTemporary(state, 1024, node.location); result = calendarFromOrdinal(ordinal(left) + right, node.location); }
  else if (id === "date_diff") { spend(state, 2, node.location); result = ordinal(right) - ordinal(left); }
  else expressionFail("UNSUPPORTED", node.location, "Unsupported compiled expression.");
  return typeof result === "number" ? finiteResult(result, node.location) : result;
}
function finiteResult(value, location) {
  if (!Number.isFinite(value)) expressionFail("RANGE", location, "Expression numeric result must be finite.");
  return Object.is(value, -0) ? 0 : value;
}
function registerPlan(plan, state) {
  const compiled = plan?.[compiledKey];
  if (!compiled) expressionFail("INVALID", "$.plan", "Expected a compiled native expression.");
  if (!state.plans.has(plan)) {
    const reduced = Object.fromEntries(Object.keys(state.limits).map(key => [key, Math.min(state.limits[key], compiled.limits[key])]));
    if (Object.keys(reduced).some(key => reduced[key] !== state.limits[key])) {
      for (const previous of state.plans) {
        const existing = previous[compiledKey];
        if (existing.canonical) parseTypedRoot(existing.canonical, reduced);
        else existing.validate(reduced);
      }
    }
    state.limits = reduced;
    if (state.work > state.limits.work || state.bytes > state.limits.retainedBytes || state.nodes > state.limits.definitionNodes) expressionFail("LIMIT", "$.plan", "Execution exceeds the reduced plan limits.");
    if (compiled.canonical) parseTypedRoot(compiled.canonical, state.limits);
    else compiled.validate(state.limits);
    if (state.nodes + compiled.nodes > state.limits.definitionNodes) expressionFail("LIMIT", "$.plan", "Execution definition-node limit exceeded.");
    reserve(state, compiled.bytes, "$.plan");
    state.nodes += compiled.nodes; state.bytes += compiled.bytes; state.plans.add(plan);
  }
  return compiled;
}
function accountResult(value, state) {
  const bytes = 64 + (typeof value === "string" ? value.length * 2 : 8);
  reserve(state, bytes, "$.result"); state.bytes += bytes;
  return value;
}
export function createTypedExpressionSession(options = {}) {
  if (typeof globalThis.BigInt !== "function") expressionFail("RUNTIME_UNAVAILABLE", "$.runtime", "Typed runtime requires native BigInt support for deterministic rounding.");
  const state = { limits: reducedLimits(options.limits), work: 0, bytes: 0, temporaryBytes: 0, nodes: 0, plans: new Set(), failed: false, parameterInputs: new WeakMap(), parameterDefinitions: new Set(), currentParameters: Object.create(null) };
  return Object.freeze({
    [sessionKey]: state,
    evaluate(plan, context = {}, calculations = Object.create(null)) {
      return inTypedExecution(state, context, () => {
        const compiled = registerPlan(plan, state);
        prepareParameters(compiled.parameters, context, state);
        spend(state, 1, "$.context");
        return accountResult(runTyped(compiled.tree, context, calculations, state), state);
      });
    },
    get work() { return state.work; },
    get accountedBytes() { return state.bytes; },
  });
}
function prepareParameters(parameters, context, state) {
  if (parameters.length && !state.parameterDefinitions.has(parameters)) {
    const bytes = JSON.stringify(parameters).length * 2;
    reserve(state, bytes, "$.parameters"); state.bytes += bytes;
    spend(state, parameters.length, "$.parameters");
    state.parameterDefinitions.add(parameters);
  }
  const inputs = context.parameters;
  if (inputs === undefined) { state.currentParameters = resolveReportParameterValues(parameters); return; }
  if (!inputs || typeof inputs !== "object" || !Object.isFrozen(inputs)) { state.currentParameters = resolveReportParameterValues(parameters, inputs); return; }
  let byDefinition = state.parameterInputs.get(inputs);
  if (!byDefinition) { byDefinition = new Map(); state.parameterInputs.set(inputs, byDefinition); }
  if (!byDefinition.has(parameters)) byDefinition.set(parameters, resolveReportParameterValues(parameters, inputs));
  state.currentParameters = byDefinition.get(parameters);
}
function inTypedExecution(state, context, evaluate) {
  if (state.failed) expressionFail("DATA", "$.session", "Expression execution has already failed.");
  try { typedKeys(context, [...expressionScopes, "parameters"], "$.context"); return evaluate(); }
  catch (error) { state.failed = true; throw error; }
  finally { state.temporaryBytes = 0; }
}
export function evaluateTypedExpression(root, context = {}, options = {}) {
  return createTypedExpressionSession(options).evaluate(compileTypedExpression(root, options), context);
}

export function compileTypedCalculations(declarations, options = {}) {
  const limits = reducedLimits(options.limits);
  const parameters = compileReportParameters(options.parameters);
  typedObject(declarations, "$.calculations");
  const ids = Object.keys(declarations);
  if (ids.length > limits.declarations) expressionFail("LIMIT", "$.calculations", "Declaration limit exceeded.");
  ids.forEach(id => typedPath(id, "$.calculations", limits, true));
  const fields = fieldCatalog(options.fields || {}, limits);
  if (ids.some(id => Object.keys(fields.detail || {}).some(path => path === id || path.startsWith(`${id}.`)))) expressionFail("REFERENCE", "$.calculations", "Calculated identities cannot shadow source fields or their containers.");
  const parsed = Object.create(null); const legacy = []; const typesById = Object.create(null);
  const edges = new Map(); let nodes = 0; let dependencies = 0; let typedCount = 0;
  for (const id of ids) {
    const expression = declarations[id];
    typedObject(expression, "$.calculations");
    if (ownTyped(expression, "typed")) {
      if (++typedCount > limits.calculations) expressionFail("LIMIT", "$.calculations", "Typed declaration limit exceeded.");
      parsed[id] = parseTypedRoot(expression, limits);
      edges.set(id, [...parsed[id].references]);
      nodes += parsed[id].nodes; dependencies += parsed[id].references.size;
    } else legacy.push(id);
  }
  function numericLegacy(value, current, depth, seen = new Set()) {
    if (++nodes > limits.definitionNodes || depth > limits.depth || seen.has(value)) expressionFail("LIMIT", "$.calculations", "Legacy dependency complexity exceeded.");
    typedObject(value, "$.calculations"); seen.add(value);
    const kinds = ["const", "field", "op", "aggregate"].filter(key => ownTyped(value, key));
    if (kinds.length !== 1) expressionFail("MIXED_MODE", "$.calculations", "Legacy dependency must be provably numeric.");
    if (kinds[0] === "const") {
      typedKeys(value, ["const"], "$.calculations");
      if (typeof value.const !== "number" || !Number.isFinite(value.const)) expressionFail("MIXED_MODE", "$.calculations", "Legacy dependency must be provably numeric.");
    } else if (kinds[0] === "field") {
      typedKeys(value, ["field"], "$.calculations"); typedPath(value.field, "$.calculations", limits);
      if (ownTyped(parsed, value.field)) expressionFail("MIXED_MODE", "$.calculations", "Legacy calculations cannot depend on typed results.");
      if (ids.includes(value.field)) {
        if (legacy.indexOf(value.field) >= legacy.indexOf(current)) expressionFail("MIXED_MODE", "$.calculations", "Legacy dependencies retain authored ordering.");
        edges.get(current).push(value.field); dependencies++;
      } else if (fields.detail?.[value.field]?.type !== "number") expressionFail("MIXED_MODE", "$.calculations", "Legacy source must have a numeric contract.");
    } else {
      typedKeys(value, ["op", "args", "left", "right"], "$.calculations");
      if (!ownTyped(arithmetic, value.op) || (ownTyped(value, "args") && (ownTyped(value, "left") || ownTyped(value, "right")))) expressionFail("MIXED_MODE", "$.calculations", "Legacy dependency is ambiguous.");
      const args = ownTyped(value, "args") ? typedArray(value.args, limits.arguments, "$.calculations") : [value.left, value.right].filter(item => item !== undefined);
      const [minimum, maximum] = arithmetic[value.op];
      if (args.length < minimum || args.length > maximum) expressionFail("MIXED_MODE", "$.calculations", "Legacy dependency arity is unsupported.");
      args.forEach(arg => numericLegacy(arg, current, depth + 1, seen));
    }
    seen.delete(value);
  }
  const typedNodes = nodes;
  for (const id of legacy) {
    const start = nodes;
    edges.set(id, []); numericLegacy(declarations[id], id, 1); typesById[id] = typeInfo("number", true);
    if (nodes - start > limits.nodes) expressionFail("LIMIT", "$.calculations", "Legacy expression node limit exceeded.");
  }
  if (nodes > limits.definitionNodes || dependencies > limits.dependencies) expressionFail("LIMIT", "$.calculations", "Calculation graph budget exceeded.");
  for (const refs of edges.values()) for (const ref of refs) if (!ids.includes(ref)) expressionFail("REFERENCE", "$.calculations", "Missing calculated declaration.");
  const depths = new Map(); const visiting = new Set();
  function dependencyDepth(id, depth) {
    if (visiting.has(id)) expressionFail("CYCLE", "$.calculations", "Calculated dependency cycle.");
    if (depth > limits.dependencyDepth) expressionFail("LIMIT", "$.calculations", "Calculation dependency depth exceeded.");
    if (depths.has(id)) return depths.get(id);
    visiting.add(id);
    const result = 1 + Math.max(0, ...edges.get(id).map(ref => dependencyDepth(ref, depth + 1)));
    visiting.delete(id); depths.set(id, result);
    if (result > limits.dependencyDepth) expressionFail("LIMIT", "$.calculations", "Calculation dependency depth exceeded.");
    return result;
  }
  ids.forEach(id => dependencyDepth(id, 1));
  const order = [...legacy]; const plans = Object.create(null);
  const pending = ids.filter(id => ownTyped(parsed, id));
  while (pending.length) {
    const index = pending.findIndex(id => edges.get(id).every(ref => ownTyped(typesById, ref)));
    if (index < 0) expressionFail("CYCLE", "$.calculations", "Calculated dependency cycle.");
    const [id] = pending.splice(index, 1);
    plans[id] = compileParsed(parsed[id], fields, ["detail", "document"], typesById, limits, parameters);
    typesById[id] = plans[id].type; order.push(id);
  }
  const legacyCopies = Object.fromEntries(legacy.map(id => [id, freezeTree(JSON.parse(JSON.stringify(declarations[id])))]));
  const legacyLedger = legacy.length ? { [compiledKey]: {
    limits, nodes: nodes - typedNodes, bytes: 64 + (nodes - typedNodes) * 128 + JSON.stringify(legacyCopies).length * 2,
    validate(reduced) { compileTypedCalculations(legacyCopies, { fields, limits: reduced }); },
  } } : null;
  return Object.freeze({
    order: Object.freeze(order), types: Object.freeze(typesById), nodes,
    canonical: freezeTree(Object.fromEntries(ids.map(id => [id, plans[id]?.canonical || legacyCopies[id]]))),
    compile(root) { return compileParsed(parseTypedRoot(root, limits), fields, ["detail", "document"], typesById, limits, parameters); },
    evaluate(context, session = createTypedExpressionSession({ limits })) {
      const state = session?.[sessionKey];
      if (!state) expressionFail("INVALID", "$.session", "Expected an execution-local typed session.");
      return inTypedExecution(state, context, () => {
        prepareParameters(parameters, context, state);
        if (legacyLedger) registerPlan(legacyLedger, state);
        spend(state, 1, "$.calculations");
        const values = Object.create(null);
        const legacyRow = Object.create(null);
        for (const id of legacy) {
          function rawInputs(node) {
            if (node.field && !ownTyped(legacyCopies, node.field)) {
              const descriptor = fields.detail[node.field];
              const value = readTypedField(context, { field: node.field, scope: "detail", result: descriptor, location: "$.calculations" }, state);
              const parts = node.field.split("."); let target = legacyRow;
              parts.slice(0, -1).forEach(part => { target = target[part] ||= Object.create(null); });
              target[parts.at(-1)] = value;
            }
            if (node.args) node.args.forEach(rawInputs);
            else { if (node.left) rawInputs(node.left); if (node.right) rawInputs(node.right); }
          }
          rawInputs(legacyCopies[id]);
          spend(state, 256, "$.calculations");
          const value = evaluateNativeExpression(legacyCopies[id], { row: legacyRow });
          if (value !== null && (typeof value !== "number" || !Number.isFinite(value))) expressionFail("DATA", "$.calculations", "Legacy dependency result is not numeric.");
          legacyRow[id] = value; values[id] = value;
        }
        for (const id of order.filter(id => ownTyped(plans, id))) {
          const compiled = registerPlan(plans[id], state);
          values[id] = runTyped(compiled.tree, context, values, state);
        }
        for (const value of Object.values(values)) accountResult(value, state);
        return Object.freeze(values);
      });
    },
  });
}
